'use client';

import { useGLTF } from '@react-three/drei';
import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import * as THREE from 'three';
import {
  cloneMaterialMapsForPlayer,
  getPlayerTextureQualityEpoch,
  isPlayerTextureQualityPassive,
  subscribePlayerTextureQuality,
} from './player-texture-quality';

export interface InstanceTransform {
  id: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  visible: boolean;
}

export interface InstancedGlbGroupProps {
  url: string;
  instances: InstanceTransform[];
  castShadow?: boolean;
  receiveShadow?: boolean;
  onClick?: (instanceId: string, e?: any) => void;
  onPointerOver?: (instanceId: string) => void;
  onPointerOut?: (instanceId: string) => void;
}

interface MeshData {
  geometry: THREE.BufferGeometry;
  material: THREE.Material | THREE.Material[];
}

function collectMeshData(scene: THREE.Object3D): MeshData[] {
  const result: MeshData[] = [];
  scene.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      result.push({ geometry: child.geometry, material: child.material });
    }
  });
  return result;
}

export function InstancedGlbGroup({
  url,
  instances,
  castShadow = true,
  receiveShadow = true,
  onClick,
  onPointerOver,
  onPointerOut,
}: InstancedGlbGroupProps) {
  const { scene } = useGLTF(url);
  const textureQualityEpoch = useSyncExternalStore(
    subscribePlayerTextureQuality,
    getPlayerTextureQualityEpoch,
    getPlayerTextureQualityEpoch,
  );
  const groupRef = useRef<THREE.Group>(null);
  const meshRefs = useRef<THREE.InstancedMesh[]>([]);

  const meshDataList = useMemo(() => {
    const raw = collectMeshData(scene);
    if (isPlayerTextureQualityPassive()) return raw;
    return raw.map((md) => ({
      geometry: md.geometry,
      material: Array.isArray(md.material)
        ? md.material.map((m) => cloneMaterialMapsForPlayer(m))
        : cloneMaterialMapsForPlayer(md.material),
    }));
  }, [scene, textureQualityEpoch]);

  const visibleInstances = useMemo(() => instances.filter((i) => i.visible), [instances]);
  const count = visibleInstances.length;

  useEffect(() => {
    if (count === 0) return;

    const dummy = new THREE.Object3D();
    for (const imesh of meshRefs.current) {
      if (!imesh) continue;
      for (let i = 0; i < count; i++) {
        const inst = visibleInstances[i];
        dummy.position.set(...inst.position);
        dummy.rotation.set(...inst.rotation);
        dummy.scale.set(...inst.scale);
        dummy.updateMatrix();
        imesh.setMatrixAt(i, dummy.matrix);
      }
      imesh.instanceMatrix.needsUpdate = true;
    }
  }, [visibleInstances, count]);

  if (count === 0 || meshDataList.length === 0) return null;

  return (
    <group ref={groupRef}>
      {meshDataList.map((md, idx) => (
        <instancedMesh
          key={idx}
          ref={(el: THREE.InstancedMesh) => { if (el) meshRefs.current[idx] = el; }}
          args={[md.geometry, Array.isArray(md.material) ? md.material[0] : md.material, count]}
          castShadow={castShadow}
          receiveShadow={receiveShadow}
          onClick={(e: any) => {
            if (onClick && e.instanceId != null && e.instanceId < visibleInstances.length) {
              e.stopPropagation();
              onClick(visibleInstances[e.instanceId].id, e);
            }
          }}
          onPointerOver={(e: any) => {
            if (onPointerOver && e.instanceId != null && e.instanceId < visibleInstances.length) {
              onPointerOver(visibleInstances[e.instanceId].id);
            }
          }}
          onPointerOut={(e: any) => {
            if (onPointerOut && e.instanceId != null && e.instanceId < visibleInstances.length) {
              onPointerOut(visibleInstances[e.instanceId].id);
            }
          }}
        />
      ))}
    </group>
  );
}
